import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from '../../../../components/Iconify';
import Scrollbar from '../../../../components/Scrollbar';
import { TableEmptyRows, TableHeadCustom, TableNoData, TableSkeleton } from '../../../../components/table';
import useTable, { emptyRows } from '../../../../hooks/useTable';
import { adminApi } from '../../../../utils/axios';
import { USER_KYC, USER_KYC_HIGHLIGHTS } from '../../../../utils/apiUrl';
import { fCustomDate } from '../../../../utils/formatTime';
import RetailKycFilter from './retailKycFilter';
import RetailKycHeaders from './retailKycHeaders';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { PATH_AFTER_LOGIN } from '../../../../config';
import { PATH_ACCOUNT } from '../../../../routes/paths';
import RetailKycListTableRow from './retailKycListTableRow';
import useSettings from '../../../../hooks/useSettings';

const TABLE_HEAD = [
  { id: 'date', label: 'Date/Time', align: 'left' },
  { id: 'id', label: 'ID', align: 'left' },
  { id: 'name', label: 'Name', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left' },
  { id: 'email', label: 'Email', align: 'left' },
  { id: 'document_type', label: 'Document Type', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'date_last', label: 'Date Last Updated', align: 'left' },
  { id: '', label: '', align: 'left' },
];

const RetailKycList = () => {
  // Getting Current Date
  const currentDate = new Date();

  // Getting Date which is 30 Days back from current date
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(currentDate.getDate() - 30);

  const { dense, page, order, orderBy, rowsPerPage, setPage, onChangeDense, onChangePage, onChangeRowsPerPage } =
    useTable();

  const { themeStretch } = useSettings();

  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState({ count: 0, data: [] });
  const [filterParameters, setFilterParameters] = useState({
    search: '',
    status: '',
    document_type: '',
    from_date: fCustomDate(new Date(thirtyDaysAgo)),
    to_date: fCustomDate(new Date(currentDate)),
  });
  const [totalData, setTotalData] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getUserKycListData();
  }, [rowsPerPage, page, filterParameters]);

  useEffect(() => {
    getKycHighlightsData();
  }, []);

  // Getting Bill Payments Data
  const getUserKycListData = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.get(USER_KYC, {
        params: {
          offset: rowsPerPage * page,
          limit: rowsPerPage,
          ...filterParameters,
        },
      });
      console.log(response, 'response');
      setIsLoading(false);
      setTableData(response?.data);
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(error?.message, { variant: 'error' });
      setTableData(error);
    }
  };

  // Getting KYC Hightlights Data
  const getKycHighlightsData = async () => {
    try {
      const response = await adminApi.get(USER_KYC_HIGHLIGHTS);
      setTotalData(response?.data?.data);
    } catch (error) {
      console.log('error', error);
    }
  };

  // If there is no data
  const isNotFound = tableData?.results?.length === 0;

  // Check Dense Height
  const denseHeight = dense ? 52 : 72;

  console.log('KYC highlights', totalData);

  return (
    <Page title="Retail KYC: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Retail KYC"
          links={[{ name: 'Accounts', href: PATH_AFTER_LOGIN }, { name: 'List' }]}
          action={
            <Button
              variant="contained"
              component={RouterLink}
              to={PATH_ACCOUNT.retailKycTable.new}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              New KYC
            </Button>
          }
        />
        <Card sx={{ margin: '40px 0px' }}>
          {/* Bill Payments Filter */}
          <RetailKycFilter
            filterParameters={filterParameters}
            onFilterChange={(val) => {
              setFilterParameters(val);
              setPage(0);
            }}
          />

          {/* Bill Payments Headers */}
          {totalData && <RetailKycHeaders totalData={totalData} />}

          {/* Bill Payments Data Table */}
          <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
            <Scrollbar>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom order={order} orderBy={orderBy} headLabel={TABLE_HEAD} rowCount={tableData.count} />
                {isLoading ? (
                  <>
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                  </>
                ) : (
                  <TableBody>
                    {tableData?.results?.map((row) => (
                      <RetailKycListTableRow key={row.id} row={row} />
                    ))}
                    <TableEmptyRows height={denseHeight} emptyRows={emptyRows(page, rowsPerPage, tableData.count)} />
                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                )}
              </Table>
            </Scrollbar>
          </TableContainer>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={tableData.count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default RetailKycList;
